import {
  Decimal,
  type CreditLineSummary,
  type CreditPayment,
  type CreditPaymentType,
} from '@paguz/contracts';
import {
  AmountInput,
  Button,
  Label,
  MoneyText,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from '@paguz/ui';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheckIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { confirmCreditPayment, createCreditPayment } from '../../api/credit.ts';
import { customerMessage } from '../../api/errors.ts';
import { newIdempotencyKey } from '../../api/sca.ts';
import { InlineAlert } from '../../components/feedback.tsx';
import { ScaSheet } from '../../components/sca-sheet.tsx';
import { useMe } from '../profile/profile-page.tsx';

type Option = 'MINIMUM' | 'TOTAL' | 'CUSTOM';
type Step = 'closed' | 'choose' | 'sca' | 'done';

/** Pago de cuota (P1): mínimo, total u otro valor, confirmado con SCA. */
export function PayInstallmentSheet({
  line,
  open,
  onOpenChange,
}: {
  line: CreditLineSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const me = useMe();
  const [step, setStep] = useState<Step>('choose');
  const [option, setOption] = useState<Option>('MINIMUM');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState<CreditPayment | null>(null);
  const keys = useRef({ create: newIdempotencyKey(), confirm: newIdempotencyKey() });

  const account = me.data?.linkedAccounts.find((a) => a.isDefault) ?? me.data?.linkedAccounts[0];
  const amount =
    option === 'MINIMUM'
      ? line.minimumPayment.amount
      : option === 'TOTAL'
        ? line.payoffAmount.amount
        : custom;
  const validAmount =
    amount !== '' && new Decimal(amount).gt(0) && new Decimal(amount).lte(line.payoffAmount.amount);

  function reset() {
    setStep('choose');
    setPayment(null);
    setError(null);
    keys.current = { create: newIdempotencyKey(), confirm: newIdempotencyKey() };
  }

  async function startPayment() {
    if (!account || !validAmount) return;
    setBusy(true);
    setError(null);
    try {
      const type: CreditPaymentType = option;
      const created = await createCreditPayment(
        { lineId: line.id, amount, type, sourceAccountId: account.id },
        keys.current.create,
      );
      setPayment(created);
      setStep('sca');
    } catch (err) {
      setError(customerMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const sheetOpen = open && (step === 'choose' || step === 'done');

  return (
    <>
      <Sheet
        open={sheetOpen}
        onOpenChange={(next) => {
          if (!next) {
            onOpenChange(false);
            if (step === 'done') reset();
          }
        }}
      >
        <SheetContent className="mx-auto max-w-[480px]">
          {step === 'done' && payment ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <CircleCheckIcon className="size-6 text-success" aria-hidden />
                  ¡Recibimos tu pago!
                </SheetTitle>
                <SheetDescription>Así se aplicó a tu crédito:</SheetDescription>
              </SheetHeader>
              <dl className="flex flex-col gap-2 px-5 pt-2">
                {payment.allocation &&
                  (
                    [
                      ['Intereses', payment.allocation.interest.amount],
                      ['Seguro', payment.allocation.insurance.amount],
                      ['Comisiones', payment.allocation.fees.amount],
                      ['Capital', payment.allocation.principal.amount],
                      ['Saldo a favor', payment.allocation.creditBalance.amount],
                    ] as const
                  )
                    .filter(([, value]) => !new Decimal(value).isZero())
                    .map(([label, value]) => (
                      <div key={label} className="flex justify-between">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd>
                          <MoneyText value={value} />
                        </dd>
                      </div>
                    ))}
              </dl>
              <SheetFooter>
                <Button
                  size="lg"
                  onClick={() => {
                    onOpenChange(false);
                    reset();
                  }}
                >
                  Listo
                </Button>
              </SheetFooter>
            </>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle>Pagar mi cuota</SheetTitle>
                <SheetDescription>
                  Se debita de tu cuenta{' '}
                  {account ? `${account.participantName} ${account.maskedNumber}` : 'vinculada'}.
                </SheetDescription>
              </SheetHeader>
              <fieldset className="flex flex-col gap-2 px-5 pt-2">
                <legend className="sr-only">¿Cuánto quieres pagar?</legend>
                {(
                  [
                    ['MINIMUM', 'Pago mínimo', line.minimumPayment.amount],
                    ['TOTAL', 'Pago total', line.payoffAmount.amount],
                    ['CUSTOM', 'Otro valor', null],
                  ] as const
                ).map(([value, label, total]) => (
                  <label
                    key={value}
                    className={cn(
                      'flex min-h-14 cursor-pointer items-center gap-3 rounded-lg border px-4',
                      option === value && 'border-primary bg-accent',
                    )}
                  >
                    <input
                      type="radio"
                      name="pay-option"
                      value={value}
                      checked={option === value}
                      onChange={() => {
                        setOption(value);
                      }}
                      className="size-5 accent-primary"
                    />
                    <span className="flex-1 font-medium">{label}</span>
                    {total !== null && <MoneyText value={total} />}
                  </label>
                ))}
                {option === 'CUSTOM' && (
                  <div className="mt-2 flex flex-col gap-2">
                    <Label htmlFor="custom-amount">Valor a pagar</Label>
                    <AmountInput
                      id="custom-amount"
                      value={custom}
                      onValueChange={setCustom}
                      invalid={custom !== '' && !validAmount}
                    />
                  </div>
                )}
                {error !== null && <InlineAlert tone="danger">{error}</InlineAlert>}
                {!account && me.isSuccess && (
                  <InlineAlert tone="warning">
                    No tienes una cuenta vinculada para debitar el pago.
                  </InlineAlert>
                )}
              </fieldset>
              <SheetFooter>
                <Button
                  size="lg"
                  loading={busy}
                  disabled={!validAmount || !account}
                  onClick={() => void startPayment()}
                >
                  Continuar
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ScaSheet
        open={open && step === 'sca'}
        onOpenChange={(next) => {
          if (!next) setStep('choose');
        }}
        challengeId={payment?.scaChallengeId ?? null}
        title="Autoriza tu pago"
        summary={
          <div className="flex items-baseline justify-between rounded-lg bg-muted p-3">
            <span className="text-muted-foreground">Vas a pagar</span>
            <MoneyText value={amount === '' ? '0' : amount} size="lg" />
          </div>
        }
        onRestart={reset}
        onVerified={async (scaToken) => {
          if (!payment) return;
          const applied = await confirmCreditPayment(payment.id, scaToken, keys.current.confirm);
          setPayment(applied);
          setStep('done');
          toast.success('Pago aplicado a tu crédito');
          await queryClient.invalidateQueries({ queryKey: ['credit'] });
        }}
      />
    </>
  );
}
